"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReachIamTokenRetrievalInstance = void 0;
const axios_1 = require("axios");
const errors_1 = require("./errors");
const utility_1 = require("./utility");
const DEFAULT_AUTH_URL = 'https://api.reachcm.com/auth/token';
class ReachIamTokenRetrievalInstance {
    constructor() {
        this._tokens = {};
        this._authServerTokenUrl = DEFAULT_AUTH_URL;
        this._timeProvider = ReachIamTokenRetrievalInstance.defaultTimeProvider;
    }
    /**
     * Default time provider. Returns Date.now().
     */
    static async defaultTimeProvider() {
        return Date.now();
    }
    /**
     * Sets a custom time provider that is used to determine the epoch
     * timestamp used to compute TOTP secrets.
     */
    setTimeProvider(provider) {
        this._timeProvider = provider;
        return this;
    }
    /**
     * Set the secrets used to retrieve a token. (Required)
     *
     * @throws {@link OverwriteConfiguration} when the secrets have already been
     * set.
     */
    setSecrets(options) {
        if (this._secrets) {
            throw new errors_1.OverwriteConfiguration('Cannot reconfigure the client\'s secrets.');
        }
        this._secrets = options;
        return this;
    }
    /**
     * Override the authentication endpoint used. (Optional)
     *
     * @throws {@link OverwriteConfiguration} when the authorization endpoint
     * has previously been overridden.
     */
    setEndpoint(url) {
        if (this._authServerTokenUrl !== DEFAULT_AUTH_URL) {
            throw new errors_1.OverwriteConfiguration('Cannot reconfigure the authorization endpoint.');
        }
        this._authServerTokenUrl = url;
        return this;
    }
    /**
     * Set the identity used to retrieve the player token. (Semi-Optional)
     *
     * *Note:* If not set, the identity will need to pass in when calling
     * {@link ReachIamTokenRetrievalInstance.retrievePlayerToken}.
     *
     * @param uuid - The player's UUID.
     * @param hardwareId - The unique identifier of the player.
     * @throws {@link OverwriteConfiguration} when the player's identity has
     * already been set.
     */
    setPlayerId(uuid, hardwareId) {
        if (this._playerIdentity) {
            throw new errors_1.OverwriteConfiguration('Cannot reconfigure the player\'s identity.');
        }
        this._playerIdentity = {
            type: 'player',
            name: uuid,
            id: hardwareId,
        };
        return this;
    }
    /**
     * Retrieve IAM token from the API if stored token is expired.
     *
     * @throws {@link MissingConfiguration} when the secrets are not set.
     * @todo Implement stampede protection.
     * @todo Extract token expiration from the JWT rather than using the TTL.
     * @todo Make the caching mechanism such that the code will initiate a
     * re-auth 5 minutes prior to expiration and re-use the soon-to-expire token
     * in the interim. This way, callers won’t often have to wait for a token
     * re-auth as it will happen in a non-blocking way.
     */
    async _retrieve(identity) {
        if (!this._secrets) {
            throw new errors_1.MissingConfiguration('The secrets must be set.');
        }
        const userId = utility_1.encodeUserId(identity);
        if (!this._tokens[userId] || Date.now() > this._tokens[userId].expires) {
            const response = await axios_1.default.post(this._authServerTokenUrl, new URLSearchParams({
                client_id: this._secrets.clientId,
                client_secret: this._secrets.clientSecret,
                redirect_uri: 'https://dev/null',
                scope: 'openid',
                grant_type: 'password',
                username: userId,
                password: utility_1.computeTotpFromSharedSecret(this._secrets.sharedSecret, userId, await this._timeProvider()),
            }), {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    // query URL without using browser cache
                    'Cache-Control': 'no-cache',
                },
            });
            // being cautious, remove 30 seconds to mitigate travel time
            const ttlInSecs = response.data.expires_in - 30;
            const expires = Date.now() + (ttlInSecs * 1000);
            this._tokens[userId] = {
                accessToken: response.data.access_token,
                expires,
            };
        }
        return this._tokens[userId];
    }
    async retrievePlayerToken(uuid, hardwareId) {
        let identity;
        if (uuid && hardwareId) {
            identity = {
                type: 'player',
                name: uuid,
                id: hardwareId,
            };
        }
        else if (this._playerIdentity) {
            identity = this._playerIdentity;
        }
        else {
            throw new errors_1.MissingConfiguration('The player\'s identity must be set.');
        }
        const iamData = await this._retrieve(identity);
        return iamData.accessToken;
    }
}
exports.ReachIamTokenRetrievalInstance = ReachIamTokenRetrievalInstance;
//# sourceMappingURL=reachIamToken.js.map